import { makeStyles } from "@mui/styles";
import { IconButton as MuiIconButton, TooltipProps } from "@mui/material";
import { useState } from "react";
import { ColorOptions, IconButtonContainedProps } from "./IconButtonContained";
import { WhiteOnBlackTooltip } from "./CustomTooltip";

const useStyles = makeStyles((theme) => ({
  root: {
    border: `2px solid ${theme.palette.divider}`,
    boxSizing: "border-box",
    backgroundColor: "#FFFFFF",
    transitionProperty: "transform, color, border-color, background-color",
    transitionDuration: "250ms",
    transitionTimingFunction: "cubic-bezier(0.4, 0, 0.2, 1)",
    "&:disabled": {
      borderColor: theme.palette.divider,
      color: theme.palette.neutral.dark,
    },
  },
  zoom: {
    "&:hover": {
      transform: "scale(1.02)",
    },
    "&:active": {
      transform: "scale(0.95)",
      transitionDuration: "32ms",
      transitionTimingFunction: "ease-in-out",
    },
  },
  primary: {
    color: theme.palette.primary.main,
    borderColor: theme.palette.primary.main,
    "&:hover": {
      borderColor: theme.palette.primary.main,
      backgroundColor: theme.palette.primary.light,
    },
  },
  secondary: {
    color: theme.palette.secondary.main,
    borderColor: theme.palette.secondary.main,
    "&:hover": {
      borderColor: theme.palette.secondary.main,
      backgroundColor: theme.palette.secondary.light,
    },
  },
  error: {
    color: theme.palette.error.main,
    borderColor: theme.palette.error.main,
    "&:hover": {
      borderColor: theme.palette.error.main,
      backgroundColor: theme.palette.error.light,
    },
  },
  info: {
    color: theme.palette.info.main,
    borderColor: theme.palette.info.main,

    "&:hover": {
      borderColor: theme.palette.info.main,
      backgroundColor: theme.palette.info.light,
    },
  },
  success: {
    color: theme.palette.success.main,
    borderColor: theme.palette.success.main,

    "&:hover": {
      borderColor: theme.palette.success.main,
      backgroundColor: theme.palette.success.light,
    },
  },
  warning: {
    color: theme.palette.warning.main,
    borderColor: theme.palette.warning.main,

    "&:hover": {
      borderColor: theme.palette.warning.main,
      backgroundColor: theme.palette.warning.light,
    },
  },
  neutral: {
    color: theme.palette.neutral.contrastText,
    borderColor: theme.palette.neutral.main,
    "&:hover": {
      color: theme.palette.primary.main,
      borderColor: theme.palette.primary.main,
      backgroundColor: theme.palette.primary.light,
    },
  },
  flush: {
    backgroundColor: "transparent",
    color: theme.palette.neutral.contrastText,
    borderColor: "transparent",
    "&:hover": {
      backgroundColor: "transparent",
      color: theme.palette.neutral.contrastText,
      borderColor: "transparent",
      // borderColor: theme.palette.primary.main,
      // color: theme.palette.primary.main,
      // backgroundColor: theme.palette.primary.light,
    },
  },
  none: {},
}));

interface IconButtonOutlinedProps extends IconButtonContainedProps {
  outlineColor?: ColorOptions;
}

export const IconButtonOutlined = (props: IconButtonOutlinedProps) => {
  const classes = useStyles();
  const [hover, setHover] = useState(false);
  const MuiProps = { ...props };
  delete MuiProps["color"];
  delete MuiProps["zoom"];
  delete MuiProps["hover"];
  delete MuiProps["children"];
  delete MuiProps["tooltipLabel"];
  delete MuiProps["tooltipPlacement"];

  const button = (
    <MuiIconButton
      aria-label={typeof props.tooltipLabel === "string" ? props.tooltipLabel : ""}
      {...MuiProps}
      className={`${props.className} ${classes.root} ${
        classes[props.color ?? "primary"]
      } ${props.zoom && classes.zoom}`}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}>
      {hover ? (props.hover ? props.hover : props.children) : props.children}
    </MuiIconButton>
  );

  return props.tooltipLabel ? (
    <WhiteOnBlackTooltip
      arrow
      title={props.tooltipLabel ?? ""}
      placement={props.tooltipPlacement as keyof TooltipProps["placement"]}>
      <div style={{ display: "inline-block" }}>{button}</div>
    </WhiteOnBlackTooltip>
  ) : (
    button
  );
};
